import { ChevronLeft, RemoveAllSelected } from '@/components/Icons';
import { useHover } from '@/hooks';
import {
  autoUpdate,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { t } from 'i18next';
import { Fragment, MouseEvent, ReactElement, ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

type DropdownProps = {
  isOpen: boolean;
  children?: ReactNode;
  inputValue?: string;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  error?: string;
  hiddenClassName?: string;
  buttonClassMame?: string;
  containerClassName?: string;
  placement?: Placement;
  setIsOpen: (isOpen: boolean) => void;
  onOutsideClick?: () => void;
  portal?: boolean;
  onRemoveAllSelectedClick?: () => void;
};

export const Dropdown = ({
  isOpen,
  size = 'small',
  disabled = false,
  error,
  children,
  placeholder,
  inputValue,
  hiddenClassName,
  buttonClassMame,
  containerClassName,
  placement,
  setIsOpen,
  onOutsideClick,
  portal,
  onRemoveAllSelectedClick,
}: DropdownProps): ReactElement => {
  const ref = useRef(null);
  const { isHovered, reset } = useHover(ref);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement,
    onOpenChange: (nextOpen, _, reason) => {
      setIsOpen(nextOpen);

      if (reason === 'outside-press') {
        onOutsideClick?.();
      }
    },
    whileElementsMounted: autoUpdate,
    middleware: [shift(), offset(4)],
  });

  const dismiss = useDismiss(context);

  const { getFloatingProps, getReferenceProps } = useInteractions([dismiss]);

  const FloatWrapper = portal ? FloatingPortal : Fragment;

  return (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}
      className={twMerge(
        'flex h-full w-full flex-col gap-2',
        containerClassName,
      )}
    >
      <button
        ref={ref}
        type="button"
        className={twMerge(
          'relative h-full w-full min-w-32 text-sm font-normal',
          isOpen && 'rounded-b-none',
          disabled && 'cursor-default',
          buttonClassMame,
        )}
        onClick={() => {
          {
            if (!disabled) {
              reset();
              setIsOpen(!isOpen);
            }
          }
        }}
      >
        <div
          className={twMerge(
            'flex h-full w-full items-center justify-between gap-2 rounded-[5px] border border-gray-100 bg-gray-100 p-2 text-sm font-normal text-gray-400 outline-none',
            (isOpen || isHovered) && 'border-blue-500',
            disabled && 'border-none',
          )}
        >
          <span
            className={twMerge(
              'overflow-hidden text-ellipsis whitespace-nowrap',
              !inputValue && 'italic text-gray-400',
              inputValue && 'text-gray',
              disabled && 'text-gray-300',
              (size === 'medium' || size === 'large') && 'text-base',
            )}
          >
            {inputValue ?? placeholder ?? 'Select an option'}
          </span>
          <div className="flex items-center gap-1">
            {(inputValue || placeholder != t('all_time')) &&
              onRemoveAllSelectedClick && (
                <RemoveAllSelected
                  className={twMerge('rounded-[3px] bg-gray')}
                  onClick={(event: MouseEvent) => {
                    event.stopPropagation();
                    onRemoveAllSelectedClick?.();
                  }}
                />
              )}
            <ChevronLeft
              className={twMerge(
                'size-3 -rotate-90 text-gray transition-transform',
                isOpen && 'rotate-90 text-blue-500',
                isHovered && 'text-blue-500',
                disabled && 'text-gray-300',
              )}
            />
          </div>
        </div>
      </button>
      {isOpen && (
        <FloatWrapper>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={twMerge(
              'border-primary-500 bg-primary-50 z-10 overflow-auto rounded-md rounded-t-none border border-t-0 bg-white text-sm shadow',
              error && 'border-red-500',
              hiddenClassName,
            )}
          >
            {children}
          </div>
        </FloatWrapper>
      )}
    </div>
  );
};
